import {
  Grid,
  GridColumn,
  ModernaTheme,
  TargetProvider,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";
import { Global } from "theme-ui";

import { RenderMetadata } from "@/components/RenderMetadata";
import ExitModal from "@/components/rsv/ExitModal";
import { RenderContent } from "@/components/rsv/RenderContent";
import { ExperienceProvider } from "@/context/ExperienceContext";
import { TrackAppStateProvider } from "@/context/TrackAppStateContext";
import { ExitModalProvider } from "@/hooks/useExitModal";
import { NavPanelProvider } from "@/hooks/useNavPanel";
import { useTargetProvider } from "@/hooks/useTargetProvider";
import { BlockPageProps } from "@/types/Block";
import { Experience, getExperience } from "@/utils/experience";
import { getIsProduction } from "@/utils/production";

export const getServerSideProps: GetServerSideProps<{
  content: BlockPageProps | null;
  experience: Experience | null;
  isProduction: boolean;
}> = async ({ req }) => {
  const experience = getExperience(req);
  const isProduction = getIsProduction();
  let content: { default: BlockPageProps } | undefined;

  if (experience === null) {
    return {
      props: {
        content: null,
        experience,
        isProduction
      }
    };
  }

  try {
    content = await import(`../profiles${experience?.profile}`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Invalid profile path:", e);
    return {
      notFound: true
    };
  }

  if (!content || (isProduction && !experience.isProduction)) {
    return {
      props: {
        content: null,
        experience,
        isProduction
      }
    };
  }
  return {
    props: {
      content: content.default,
      experience,
      isProduction
    }
  };
};

const Finder = ({
  content,
  experience
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { country, host } = experience || {};
  const { handleClick, resolveTarget } = useTargetProvider();

  const backgroundColor = host === "disease.education" ? "#1E2846" : "#01283C";
  const background = (
    <Global
      styles={{
        html: {
          backgroundColor
        }
      }}
    />
  );

  if (!experience || !country || !content) {
    return (
      <ThemeProvider>
        {background}
        <Grid>
          <GridColumn
            sx={{
              alignItems: "center",
              display: "flex",
              height: "100vh",
              justifyContent: "center"
            }}
          >
            <Text size="lg" as="p" color="cta01Text">
              The site is not available in your country.
            </Text>
          </GridColumn>
        </Grid>
      </ThemeProvider>
    );
  }

  return (
    <ExperienceProvider experience={experience}>
      <ThemeProvider
        theme={{
          ...ModernaTheme,
          colorsDark: {
            ...ModernaTheme.colorsDark,
            accent01: "#079AE0",
            iconButtonBackgroundColor: "#E31837",
            text04: "#FFFFFF", // checkbox icon color
            ...(host === "disease.education" && { background01: "#1E2846" })
          }
        }}
      >
        {background}
        <TargetProvider clickHandler={handleClick} targetResolver={resolveTarget}>
          <ExitModal />
          <NavPanelProvider>
            <RenderMetadata {...content.metadata} renderGoogleRecaptcha={true} />
            <RenderContent content={content as BlockPageProps} />
          </NavPanelProvider>
        </TargetProvider>
      </ThemeProvider>
    </ExperienceProvider>
  );
};

const FinderWithProviders = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { content } = props;

  if (content) {
    return (
      <ExitModalProvider exitModal={content.exitModal}>
        <TrackAppStateProvider>
          <Finder {...props} />
        </TrackAppStateProvider>
      </ExitModalProvider>
    );
  } else {
    return <Finder {...props} />;
  }
};

export default FinderWithProviders;
